import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import { Star, X } from 'lucide-react';

import { useChartTheme } from '../../../../utils/chartTheme';
import {
  getAttendanceRecords,
  getStudentDashboard,
} from '../../../../services/StudentDashboardService';
import { createStudentFeedback } from '../../../../services/StudentFeedbackService';
import { toast } from '../../common/Toast/toast';
import { silenceFor } from '../../common/Loader/busy';
import PopupDone, { usePopupDone } from '../../common/PopupDone/PopupDone';
import useScrollLoadMore from '../../common/useScrollLoadMore';
import NewCustomDataTable from '../../common/NewCustomDataTable.jsx';
import '../../Trainer/TrainerDashboard.css';
import '../../common/lms-pages.css';
// The star row below is FeedbackWidget's control, so it wears its classes
// -- and therefore needs its sheet.
import '../Feedback/feedback-widget.css';
import './attendance-record.css';

/* =============================================================
   /attendance-record -- the student's own attendance, in full.

   The Attendance shortcut on the dashboard used to open
   /attendance-calendar, which answers "what is on this month"
   rather than "what have I attended". This page answers the
   second question: every session the reader sat in, the figures
   the API worked out about them, and a way to say what the class
   was like.

   THE LIST CARRIES WHAT PLACES A SESSION -- when, which batch,
   whose class. Everything long, and the one action, live in the
   view popup the row opens (view_popup_rules): topics can run to
   a paragraph, and a column that wraps to three lines makes every
   other row unreadable to keep one of them whole.

   WHAT THIS PAGE DOES NOT DO: work anything out. The percentage,
   the totals and the per-batch counts all arrive from
   /studentdashboard already decided; the rows arrive from
   /studentdashboard/attendance-records already paged, ordered and
   stamped with this student's own feedback. Nothing here counts,
   filters or groups (hard rule 7).

   ONE HONEST LIMIT, stated rather than papered over: the records
   endpoint matches on `userIds`, so it returns the sessions the
   student was PRESENT for. Absences are not rows here, which is
   why the figures beside the chart come from the dashboard --
   they are the only place "out of how many" is known.
   ============================================================= */

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

/* chart.js ships no value labels, and with the batch names off the axis
   the bars would otherwise say nothing at all until hovered -- every
   value on a chart should be readable without hovering. The label rides
   INSIDE the bar, so it identifies the bar without turning the axis back
   into a column of names that the longest batch title sizes.

   Local to this chart, passed on the <Bar> rather than registered
   globally, so the app's other charts are untouched. It is a
   module-level object and cannot close over the theme, so its colours
   ride on the dataset (labelOnFill / labelOffFill) -- which is what
   makes a palette change repaint the labels along with the bars. Same
   shape as TeachingSummaryChart's tdsValueLabels. */
const barLabels = {
  id: 'attrecBarLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const dataset = chart.data.datasets[0] || {};
    const meta = chart.getDatasetMeta(0);
    const full = dataset.valueLabels || [];
    const short = dataset.countLabels || [];

    ctx.save();
    ctx.font = `700 12.5px ${ChartJS.defaults.font.family}`;
    ctx.textBaseline = 'middle';

    meta.data.forEach((bar, i) => {
      const name = full[i];
      const count = short[i];
      if (!name && !count) return;

      const height = Math.abs(bar.base - bar.y);

      /* The figure sits ABOVE the column, in ink. Above rather than
         inside because a one-class batch has no room inside it, and a
         figure that moves between inside and outside depending on its
         own value reads as two different charts. */
      if (count) {
        ctx.fillStyle = dataset.labelOffFill;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(count, bar.x, bar.y - 8);
      }

      /* The name runs UP the inside of the column, which is what gives
         the bar its identity without the axis carrying a row of names
         that the longest title sizes. Only when the column is tall
         enough to hold it whole: a clipped name is worse than none, and
         the full one is always a hover away. */
      if (name && height > ctx.measureText(name).width + 24) {
        ctx.save();
        ctx.translate(bar.x, bar.base - 12);
        ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = dataset.labelOnFill;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(name, 0, 0);
        ctx.restore();
      }
    });

    ctx.restore();
  },
};

const PAGE_SIZE = 25;
const MAX_STARS = 5;
/* chart.js fills its parent and maintainAspectRatio is off, so the parent
   carries both numbers. The HEIGHT is fixed -- a column chart that grows
   taller with every batch would push the list off the screen -- and the
   WIDTH is per-bar, so the columns stay readable and the panel scrolls
   sideways once there are more of them than fit. The width comes from
   data, which is the one inline style the rules allow. */
const CHART_HEIGHT = 280;
const BAR_COL = 72;

/* A rating is a number the reader is being asked to SET, so it is a row
   of radios rather than five buttons with click handlers: arrow keys
   work, the label is read out, and the group has one value. Lifted from
   FeedbackWidget, classes included, so the same question is asked the
   same way wherever it is asked. */
const Stars = ({ name, value, onChange, label, size = 'md' }) => (
  <div className={`fbw-stars fbw-stars--${size}`} role="radiogroup" aria-label={label}>
    {Array.from({ length: MAX_STARS }, (_, i) => i + 1).map((n) => (
      <label
        key={n}
        className={`fbw-star${n <= value ? ' is-on' : ''}`}
        title={`${n} of ${MAX_STARS}`}
      >
        <input
          type="radio"
          name={name}
          value={n}
          checked={value === n}
          onChange={() => onChange(n)}
        />
        <Star size={size === 'sm' ? 15 : 19} aria-hidden="true" />
        <span className="fbw-sr">{`${n} of ${MAX_STARS}`}</span>
      </label>
    ))}
  </div>
);

const StudentAttendanceRecord = () => {
  const theme = useChartTheme();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const appendRef = useRef(false);
  const listEndRef = useRef(null);

  const [dash, setDash] = useState(null);

  /* The session whose record is open, the stars on it, and whether the
     POST is in flight. null = no popup. */
  const [viewing, setViewing] = useState(null);
  /* The form, per session: a rating and a note for EACH topic, one
     rating for the trainer, and one closing comment. The session's own
     rating is not asked for -- the API derives it from the mean of the
     topics, which is the same number and one fewer thing to answer. */
  const [topicForm, setTopicForm] = useState({});
  const [trainerRating, setTrainerRating] = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  // The saved feedback is confirmed inside the record popup (PopupDone).
  const { done } = usePopupDone(Boolean(viewing));

  /* The figures and the chart's bars. Loaded once: they are about the
     whole record rather than the page of rows on screen. */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        silenceFor();
        const res = await getStudentDashboard();
        if (alive) setDash(res?.data || null);
      } catch (error) {
        console.error('Attendance record: could not load the figures', error);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const load = useCallback(async () => {
    const append = appendRef.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      silenceFor();
      const res = await getAttendanceRecords({ page, limit: PAGE_SIZE });
      const list = Array.isArray(res?.data) ? res.data : [];
      setTotalCount(res?.pagination?.total ?? list.length);
      setRows((prev) => {
        if (!append) return list;
        // A session added between two batches pushes the page boundary
        // along and repeats a row; drop any id already on screen.
        const seen = new Set(prev.map((r) => String(r.id)));
        return [...prev, ...list.filter((r) => !seen.has(String(r.id)))];
      });
    } catch (error) {
      console.error('Attendance record: could not load the sessions', error);
      toast.error('Could not load your attendance. Try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      appendRef.current = false;
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const hasMore = rows.length < totalCount;

  /* Depends on loadingMore, which moves on every batch: an observer that
     is not rebuilt after a page lands never reports the sentinel again
     (hard_rules, infinite scroll). */
  useScrollLoadMore(
    listEndRef,
    () => {
      if (loading || loadingMore || !hasMore) return;
      appendRef.current = true;
      setPage((p) => p + 1);
    },
    [rows.length, page, totalCount, loading, loadingMore],
  );

  /* Classes attended per batch, straight from the dashboard payload. One
     measure, so one series in slot 1 -- several hues would spend colour
     re-saying what the axis already says. */
  const bars = useMemo(() => {
    const list = Array.isArray(dash?.batches) ? dash.batches : [];
    return list
      .map((b) => ({
        label: b?.name || 'Unnamed batch',
        value: Number(b?.attendanceCount) || 0,
      }))
      .filter((b) => b.value > 0);
  }, [dash]);

  const chartData = useMemo(
    () => ({
      labels: bars.map((b) => b.label),
      datasets: [
        {
          label: 'Classes attended',
          data: bars.map((b) => b.value),
          backgroundColor: theme.series[0],
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 24,
          /* What the plugin above draws. Text never wears the series
             colour: on the fill it takes theme.onFill, which returns
             white only where white holds 4.5:1 on that fill and near
             black where it does not, so the label stays legible in
             every palette. Off the fill it is ordinary ink. */
          valueLabels: bars.map((b) => b.label),
          countLabels: bars.map((b) => String(b.value)),
          labelOnFill: theme.onFill(theme.series[0]),
          labelOffFill: theme.ink.body,
        },
      ],
    }),
    [bars, theme],
  );

  const chartOptions = useMemo(
    () => ({
      // Vertical columns (chart.js's default axis), one per batch.
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          ...theme.tooltip,
          callbacks: {
            /* The batch name lives HERE rather than down the axis. Spelled
               out beside every bar it becomes a column of names with a
               chart attached, and the longest name decides how little room
               the bars get. Hovering asks "which one is this?" -- this is
               the answer to that question. */
            title: (items) => items?.[0]?.label || '',
            label: (ctx) => {
              const n = ctx.parsed.y;
              return `${n} class${n === 1 ? '' : 'es'} attended`;
            },
          },
        },
      },
      scales: {
        /* The names come off the axis -- they are on the columns and in
           the tooltip -- but the axis LINE stays, so the columns have a
           baseline to stand on. */
        x: theme.categoryAxis({ ticks: { display: false } }),
        y: theme.valueAxis({ beginAtZero: true, ticks: { precision: 0 } }),
      },
    }),
    [theme],
  );

  const resetForm = (row) => {
    setTopicForm(
      Object.fromEntries(
        (row?.topics || []).map((topic) => [topic, { rating: 0, comment: '' }]),
      ),
    );
    setTrainerRating(0);
    setComment('');
  };

  const openRecord = (row) => {
    if (!row) return;
    setViewing(row);
    resetForm(row);
  };

  const closeRecord = () => {
    setViewing(null);
    resetForm(null);
  };

  const setTopic = (topic, patch) =>
    setTopicForm((prev) => ({ ...prev, [topic]: { ...prev[topic], ...patch } }));

  /* Resolved server-side into a list of topic names -- `syllabusId` is a
     comma-separated string that may hold names, legacy ObjectIds, or a
     "common off" sentence, so the raw field is not something to print. */
  const topicList = Array.isArray(viewing?.topics) ? viewing.topics : [];
  const rated = topicList.filter((t) => (topicForm[t]?.rating || 0) > 0);

  /* EVERY topic gets a star. These came OUT of the attendance record --
     the trainer recorded them as covered -- and the whole point of asking
     per topic is that "the class was a 3" does not say which part of it
     was. A partial answer would land as a row that looks complete and is
     not, so the form holds until each one has a verdict. The note beside
     each star is optional: being made to write something about every
     topic is how a form stops getting filled in at all.

     A session with no topics recorded has nothing to rate one by one, so
     there the trainer rating carries the verdict on its own. */
  const topicsRequired = topicList.length > 0;
  const canSubmit = topicsRequired
    ? rated.length === topicList.length
    : trainerRating > 0;

  const submitFeedback = async () => {
    if (!viewing || !canSubmit || saving) return;
    setSaving(true);
    try {
      /* Built from the session's own topic list, in the order it was
         recorded -- not from Object.entries of the form state, whose
         order is a map's insertion order and not something a trainer
         should have to read back. */
      const topicRatings = topicList
        .filter((topic) => (topicForm[topic]?.rating || 0) > 0)
        .map((topic) => ({
          topic,
          rating: topicForm[topic].rating,
          comment: (topicForm[topic].comment || '').trim() || undefined,
        }));

      await createStudentFeedback({
        attendanceId: viewing.id,
        /* The session rating is left out on purpose: the API derives it
           from the mean of the topics. When there are no topics to
           average, the trainer rating carries it and is sent
           explicitly. */
        ...(topicRatings.length ? { topicRatings } : { rating: trainerRating }),
        ...(trainerRating ? { trainerRating } : {}),
        topics: topicRatings.map((t) => t.topic),
        comment: comment.trim() || undefined,
      });
      /* The server accepted it, so the row and the open popup both say so
         without a refetch -- reloading the list here would throw away the
         reader's place in it. This is state the server confirmed, not a
         figure worked out in the browser. */
      /* No rating is carried back: the overall figure is the API's mean
         of the topics, so inventing one here would print a number the
         server never agreed to. The row says "Given" until it is
         reloaded, which is true. */
      const rated = { hasFeedback: true, feedback: {} };
      setRows((prev) =>
        prev.map((r) => (String(r.id) === String(viewing.id) ? { ...r, ...rated } : r)),
      );
      setViewing((v) => (v ? { ...v, ...rated } : v));
      toast.success('Thanks -- your feedback was saved.');
    } catch (error) {
      console.error('Attendance record: could not save the feedback', error);
      toast.error('Could not save your feedback. Try again.');
    } finally {
      setSaving(false);
    }
  };

  /* Four columns: when it was, whose class, and whether this reader has
     said anything about it. Topics are not here -- they are the long
     field, and they are what the popup is for. No action column either:
     the row opens the record, and the action lives in its footer
     (view_popup_rules). */
  const headers = useMemo(
    () => [
      {
        title: 'Date',
        prop: 'date',
        cell: (row) => (
          <span className="pg-date">
            {row?.date && moment(row.date).isValid()
              ? moment(row.date).format('DD MMM YYYY')
              : '—'}
          </span>
        ),
      },
      {
        title: 'Batch',
        prop: 'batchName',
        cell: (row) => <span className="pg-strong">{row?.batchName || '—'}</span>,
      },
      {
        title: 'Trainer',
        prop: 'trainerName',
        cell: (row) => <span className="lp-plain">{row?.trainerName || '—'}</span>,
      },
      {
        title: 'Your feedback',
        prop: 'hasFeedback',
        cell: (row) =>
          row?.hasFeedback ? (
            <span className="pg-pill pg-pill--good">
              {row?.feedback?.rating
                ? `${row.feedback.rating} of ${MAX_STARS}`
                : 'Given'}
            </span>
          ) : (
            <span className="pg-pill pg-pill--warn">Not given</span>
          ),
      },
    ],
    [],
  );

  const attended = dash?.classesAttended ?? null;
  const totalClasses = dash?.totalClasses ?? null;
  const percentage = dash?.attendancePercentage ?? null;

  const viewingRated = Boolean(viewing?.hasFeedback);

  return (
    <div className="dashboard-wrapper">
      <div className="pg">
        <header className="pg-head">
          <div className="pg-head-main">
            <h1 className="pg-title">My attendance</h1>
            <p className="pg-sub">
              Every class you have been marked present for, and what you said
              about it.
            </p>
          </div>
        </header>

        {/* The figures first, then the shape of them. Every one is the
            API's own -- an em dash where it has not answered, never a
            zero standing in for "not known". */}
        <div className="pgm-tiles">
          {/* The house tile styles by element, not class -- .pgm-tile b is
              the figure and .pgm-tile span the label -- so the markup is
              copied from the mock-interview popup rather than invented. */}
          <div className="pgm-tile">
            <b>{percentage === null ? '—' : `${percentage}%`}</b>
            <span>Attendance</span>
          </div>
          <div className="pgm-tile">
            <b>{attended === null ? '—' : attended}</b>
            <span>Classes attended</span>
          </div>
          <div className="pgm-tile">
            <b>{totalClasses === null ? '—' : totalClasses}</b>
            <span>Classes held</span>
          </div>
        </div>

        {bars.length > 0 && (
          <section className="td-bars">
            <header className="td-bars-head">
              <h2 className="td-bars-title">Classes attended, by batch</h2>
              <span className="td-bars-total">{dash?.attendanceBand || ''}</span>
            </header>
            {/* min-width, not width: with a few batches the chart still
                fills the panel, and only once there are more than fit
                does it run off to the right and the scroller engage. */}
            <div className="attrec-chartscroll">
              <div
                className="td-bars-canvas"
                style={{
                  height: `${CHART_HEIGHT}px`,
                  minWidth: `${bars.length * BAR_COL}px`,
                }}
              >
                <Bar data={chartData} options={chartOptions} plugins={[barLabels]} />
              </div>
            </div>
          </section>
        )}

        <div className="td-list td-list--attrec">
          <NewCustomDataTable
            headers={headers}
            data={rows}
            totalRecords={totalCount}
            currentPage={page}
            recordsPerPage={PAGE_SIZE}
            serverSide
            loading={loading}
            loadingMore={loadingMore}
            showToolbar={false}
            showPagination={false}
            showFooter={false}
            /* The whole row opens the session. Every cell wraps its text
               in a span, so the default ['TR','TD'] would leave only the
               padding clickable. */
            onRowClick={openRecord}
            validRowClickTagNames="*"
            rowProps={(row) => ({
              tabIndex: 0,
              role: 'button',
              'aria-label': `Open the class on ${
                row?.date && moment(row.date).isValid()
                  ? moment(row.date).format('DD MMM YYYY')
                  : 'this date'
              }`,
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openRecord(row);
                }
              },
            })}
          />
        </div>

        {/* Where the reader is, and the sentinel the observer watches. */}
        <div className="pg-scroll-foot" ref={listEndRef}>
          {rows.length < totalCount ? (
            <>
              <span className="pg-scroll-count">
                {rows.length} of {totalCount}
              </span>
              {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
            </>
          ) : totalCount > 0 ? (
            <span className="pg-scroll-count">
              All {totalCount} session{totalCount === 1 ? '' : 's'} loaded
            </span>
          ) : null}
        </div>

        {!loading && totalCount === 0 && (
          <div className="pg-state">
            <h5 className="pg-state-title">No attendance yet</h5>
            <p className="pg-state-text">
              Once you have been marked present in a class it will appear here.
            </p>
          </div>
        )}
      </div>

      {/* ── The session, in full ──────────────────────────────────────
          The reference popup's vocabulary, unchanged: pgm-head with the
          batch as the title and the facts that place it on one wrapping
          line, pgm-section per block, and the action in the footer with
          Close last and filled. */}
      {viewing && (
        <div className="pg-modal" role="presentation" onClick={closeRecord}>
          <div
            className="pg-modal-panel pg-modal-panel--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="attrec-view-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pgm-head">
              <div className="pgm-head-main">
                <h2 className="pgm-batch" id="attrec-view-title">
                  {viewing.batchName || 'This class'}
                </h2>
                <dl className="pgm-ident">
                  <div className="pgm-ident-row">
                    <dt>Date</dt>
                    <dd>
                      {viewing.date && moment(viewing.date).isValid()
                        ? moment(viewing.date).format('dddd, DD MMMM YYYY')
                        : '—'}
                    </dd>
                  </div>
                  <div className="pgm-ident-row">
                    <dt>Trainer</dt>
                    <dd>{viewing.trainerName || '—'}</dd>
                  </div>
                  <div className="pgm-ident-row">
                    <dt>Attendance</dt>
                    <dd>Present</dd>
                  </div>
                </dl>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                aria-label="Close"
                onClick={closeRecord}
              >
                <X size={16} />
              </button>
            </header>

            <div className="pg-modal-body">
              {done ? (
                <PopupDone message={done} onClose={closeRecord} />
              ) : (
                <>
              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  Topics covered
                  {topicList.length > 0 && (
                    <span className="pgm-section-count">{topicList.length}</span>
                  )}
                </h3>
                {viewing?.isCommonOff ? (
                  /* Not a gap in the record: a day off was never a class,
                     so there is nothing to have taught. */
                  <p className="pgm-empty">
                    This was a day off, so no topics were taught.
                  </p>
                ) : topicList.length === 0 ? (
                  <p className="pgm-empty">
                    No topics were recorded for this class.
                  </p>
                ) : (
                  <table className="pgm-table pgm-topics-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Topic</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topicList.map((t, i) => (
                        <tr key={`${t}-${i}`}>
                          <td className="pgm-num">{i + 1}</td>
                          <td>{t}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">Your feedback</h3>
                {viewingRated ? (
                  <p className="lp-prose">
                    You rated this class{' '}
                    <span className="pg-pill pg-pill--good">
                      {viewing?.feedback?.rating
                        ? `${viewing.feedback.rating} of ${MAX_STARS}`
                        : 'Given'}
                    </span>
                  </p>
                ) : (
                  <>
                    {topicList.length > 0 && (
                      <>
                        <div className="fbw-topics-head">
                          <span className="fbw-label">
                            What was covered
                            <span className="fbw-req" aria-hidden="true">
                              required
                            </span>
                          </span>
                          <span className="fbw-progress">
                            {rated.length}/{topicList.length}
                          </span>
                        </div>
                        <ul className="fbw-topics">
                          {topicList.map((topic) => (
                            <li
                              className={`fbw-topic${
                                (topicForm[topic]?.rating || 0) > 0 ? ' is-rated' : ''
                              }`}
                              key={topic}
                            >
                              <div className="fbw-topic-head">
                                <span className="fbw-topic-name">{topic}</span>
                                <Stars
                                  size="sm"
                                  name={`attrec-topic-${viewing.id}-${topic}`}
                                  label={`Rating for ${topic}`}
                                  value={topicForm[topic]?.rating || 0}
                                  onChange={(n) => setTopic(topic, { rating: n })}
                                />
                              </div>
                              {/* The note appears once the topic has a
                                  rating: an empty box under every topic
                                  reads as six things to fill in rather
                                  than six to judge. */}
                              {topicForm[topic]?.rating > 0 && (
                                <input
                                  type="text"
                                  name={`attrec-note-${viewing.id}-${topic}`}
                                  aria-label={`Note on ${topic}`}
                                  className="fbw-input"
                                  placeholder="Anything to add about this topic? (optional)"
                                  maxLength={1000}
                                  value={topicForm[topic]?.comment || ''}
                                  onChange={(e) =>
                                    setTopic(topic, { comment: e.target.value })
                                  }
                                />
                              )}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    <div className="fbw-field">
                      <span className="fbw-label">The trainer</span>
                      <Stars
                        name={`attrec-trainer-${viewing.id}`}
                        label="Rating for the trainer"
                        value={trainerRating}
                        onChange={setTrainerRating}
                      />
                      <p className="fbw-hint">
                        How it was taught, as distinct from what was covered.
                      </p>
                    </div>

                    <div className="fbw-field">
                      <label className="fbw-label" htmlFor="attrec-comment">
                        Anything else
                      </label>
                      <textarea
                        id="attrec-comment"
                        className="fbw-input fbw-textarea"
                        rows={3}
                        maxLength={2000}
                        placeholder="Optional"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </section>
                </>
              )}
            </div>

            {!done && (
            <footer className="pg-modal-foot">
              {/* What you can do with the record sits on the left; Close
                  stays last and is the only filled button. */}
              <div className="pgm-actions">
                {!viewingRated && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={submitFeedback}
                    disabled={!canSubmit || saving}
                  >
                    {saving ? 'Saving…' : 'Give feedback'}
                  </button>
                )}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={closeRecord}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAttendanceRecord;
